import RecycleItemPool from './RecycleItemPool';
import VisibilityTracker, { AllIndexes } from './VisibilityTracker';
import { Layout, Size } from './types';
import { diffArray } from './utils';

/*
 * Generates render stack for the Grid.
 * Uses VisibilityTracker for virtualization.
 * Uses RecycleItemPool for reusability (reuse keys of dis-engaged items).
 * Notifies grid view to re-render if stack changes (due to scroll offset / data change)
 */

export type RenderStackItem = {
    row: number;
    index: number;
};

export type RenderStack = {
    [key: string]: RenderStackItem;
};

export type RenderStackChangedCb = (renderStack: RenderStack) => void;

export default class VirtualRenderer {
    private _keyCounter: number = 0;

    private _recyclePool: RecycleItemPool;

    private _visibilityTracker: VisibilityTracker;

    private _idToRenderKeyMap: { [key: string]: string };

    private _renderStack: RenderStack;

    private _onRenderStackChanged: (renderStack: RenderStack) => void;

    public getStableId?: (row: number, index: number) => string;

    public onIndexesChanged?: (
        visible: number[][],
        engaged: number[][],
    ) => void;

    constructor(
        rowHeight: number,
        renderAheadOffsetHorizontal: number,
        renderAheadOffsetVertical: number,
        onRenderStackChanged: (renderStack: RenderStack) => void,
    ) {
        this._recyclePool = new RecycleItemPool();
        this._visibilityTracker = new VisibilityTracker(
            rowHeight,
            renderAheadOffsetHorizontal,
            renderAheadOffsetVertical,
            this._onIndexesChanged,
        );

        this._idToRenderKeyMap = {};
        this._renderStack = {};
        this._onRenderStackChanged = onRenderStackChanged;
    }

    public setLayouts = (layouts: Layout[][]) => {
        this._visibilityTracker.setLayouts(layouts);
    };

    public setWindowSize = (size: Size) => {
        this._visibilityTracker.setWindowSize(size);
    };

    public updateOffset = (offsetX: number, offsetY: number) => {
        this._visibilityTracker.updateOffset(offsetX, offsetY);
    };

    public reset = () => {
        this._visibilityTracker.reset();
        this._recyclePool.removeAll();
        this._renderStack = {};
    };

    private _onIndexesChanged = (indexes: AllIndexes) => {
        // We fire event first, so loading data can start (if needed)
        this._reportIndexes(indexes);

        if (indexes.engaged) {
            const {
                engaged: { nowOut, nowIn },
            } = indexes;

            let renderStackChanged = false;

            // Mark disenganged item keys (scrolled out) for recycling
            let rows = nowOut.length;
            for (let row = 0; row < rows; row += 1) {
                const rowNowOut = nowOut[row];
                const count = rowNowOut.length;
                for (let i = 0; i < count; i += 1) {
                    const index = rowNowOut[i];
                    const renderKey =
                        this._idToRenderKeyMap[this.getStableId!(row, index)];
                    if (renderKey) {
                        this._recyclePool.addKey(renderKey);
                    }
                }
            }

            // Add new items to render stack
            rows = nowIn.length;
            for (let row = 0; row < rows; row += 1) {
                const rowNowIn = nowIn[row];
                const count = rowNowIn.length;
                for (let i = 0; i < count; i += 1) {
                    const index = rowNowIn[i];
                    const id = this.getStableId!(row, index);
                    const renderKey =
                        this._recyclePool.getKey() ?? this._createKey();

                    this._idToRenderKeyMap[id] = renderKey;
                    this._renderStack[renderKey] = { row, index };

                    renderStackChanged = true;
                }
            }

            if (renderStackChanged) {
                this._onRenderStackChanged({ ...this._renderStack });
            }
        }
    };

    private _reportIndexes = (indexes: AllIndexes) => {
        if (this.onIndexesChanged) {
            const {
                visible: { now: nowV = [] } = {},
                engaged: { now: nowE = [] } = {},
            } = indexes;

            // Create an array with engaged items, which contains only
            // items that are not visible (as visible is reported in first param)
            const { length } = nowE;
            const diffNowE: number[][] = [];
            for (let i = 0; i < length; i += 1) {
                const engagedRow = nowE[i];
                const visibleRow = nowV[i] ?? [];
                const diff = diffArray(engagedRow, visibleRow);
                diffNowE.push(diff);
            }

            this.onIndexesChanged(nowV, diffNowE);
        }
    };

    private _createKey = () => {
        this._keyCounter += 1;
        return `k-${this._keyCounter}`;
    };
}
